import { useState, useCallback } from 'react';
import { marketingAuditApi } from '../api/marketingAudit';
import type { MarketingAudit } from '../api/marketingAudit';

export function useMarketingAudit() {
  const [audits, setAudits] = useState<MarketingAudit[]>([]);
  const [activeAudit, setActiveAudit] = useState<MarketingAudit | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchAudits = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingAuditApi.list(p);
      setAudits(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load marketing audits.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingAuditApi.latest();
      setActiveAudit(res.data.data);
      return res.data.data;
    } catch {
      setActiveAudit(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const runAudit = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await marketingAuditApi.run();
      const audit = res.data.data;
      setActiveAudit(audit);
      setAudits((prev) => [audit, ...prev]);
      return audit;
    } catch (err: any) {
      const msg = err?.message ?? 'Marketing audit failed.';
      setError(msg);
      return null;
    } finally {
      setRunning(false);
    }
  }, []);

  const deleteAudit = useCallback(async (id: number) => {
    try {
      await marketingAuditApi.destroy(id);
      setAudits((prev) => prev.filter((a) => a.id !== id));
      if (activeAudit?.id === id) setActiveAudit(null);
    } catch {
      setError('Failed to delete audit.');
    }
  }, [activeAudit]);

  return {
    audits, activeAudit, loading, running, error, total, page, lastPage,
    fetchAudits, fetchLatest, runAudit, deleteAudit, setActiveAudit, setError,
  };
}
